import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformAdminAuthStore } from '../../../stores/platform-admin-auth.store';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { ShieldAlert } from 'lucide-react';

export default function PlatformAdminLoginPage() {
  const navigate = useNavigate();
  const login = usePlatformAdminAuthStore((state) => state.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/platform-admin', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-orange-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-orange-200 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg">
          <div className="flex items-center justify-center mb-2">
            <ShieldAlert className="h-12 w-12" />
          </div>
          <CardTitle className="text-2xl text-center">Platform Admin</CardTitle>
          <p className="text-orange-100 text-center mt-2 text-sm">
            Business Owner Access Only
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Warning Banner */}
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
            <div className="flex">
              <ShieldAlert className="h-5 w-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-orange-800">
                  Administrative Access
                </h3>
                <p className="text-xs text-orange-700 mt-1">
                  This is a separate authentication system for platform administrators only.
                  Organization users should use the standard login.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <Input
              label="Admin Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@lentil-labs.com"
              required
              autoComplete="username"
            />

            <Input
              label="Admin Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
              isLoading={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In as Platform Admin'}
            </Button>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Default credentials (development only):
              </p>
              <p className="text-xs text-gray-600 text-center font-mono mt-1">
                admin@lentil-labs.com / admin123
              </p>
            </div>

            <div className="text-center">
              <a
                href="/login"
                className="text-sm text-gray-600 hover:text-gray-800 underline"
              >
                ← Back to organization login
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
